import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBroadcastTower } from "@fortawesome/free-solid-svg-icons";

/** CONFIG */
const ALERT_SOUND_URL = "/assets/ding-alert.mp3";
const token = localStorage.getItem("accessToken") || "";

const WEBSOCKET_URL = `wss://api.tadwiro.com/barqlytics/ws${token ? `?token=${encodeURIComponent(token)}` : ""}`;
const REST_ALERTS_BASE = "https://nginx.tadwiro.com/barqlytics/alerts";
const TV_CHART_ID = "GMM7HiCh";

/** Helpers */
const decisionClasses = (d) => {
  const v = String(d || "").toLowerCase();
  if (v === "buy") return "bg-green-50 border-l-4 border-green-500";
  if (v === "sell") return "bg-red-50 border-l-4 border-red-500";
  return "bg-slate-50 border-l-4 border-slate-400";
};
const decisionBadge = (d) => {
  const v = String(d || "").toLowerCase();
  const base = "px-2 py-0.5 rounded text-xs font-semibold inline-flex items-center gap-1";
  if (v === "buy") return `${base} bg-green-600 text-white`;
  if (v === "sell") return `${base} bg-red-600 text-white`;
  return `${base} bg-slate-600 text-white`;
};
const prettyNum = (x) => {
  if (x === null || x === undefined || x === "") return "";
  const n = Number(x);
  if (Number.isNaN(n)) return String(x);
  return Math.abs(n) >= 1000 ? n.toLocaleString() : n.toFixed(6).replace(/\.?0+$/, "");
};

function tryParsePayload(raw) {
  let obj = null;
  try { obj = JSON.parse(raw); } catch {}
  if (obj && typeof obj === "object") return obj;
  const i = raw.indexOf("{");
  if (i >= 0) { try { return JSON.parse(raw.slice(i)); } catch {} }
  return { _raw: raw };
}

function normalizeFromEnvelope(obj) {
  if (obj && obj.eventType === "alert" && obj.data && typeof obj.data === "object") {
    const d = obj.data;
    return {
      id: d.id ?? undefined,
      decision: d.decision || d.side || d.signal || "",
      ticker: d.ticker || d.symbol || "",
      exchange: d.exchange || "",
      interval: d.interval || "",
      time: d.time || d.event_time || d.timenow || d.timestamp || "",
      open: d.open, high: d.high, low: d.low, close: d.close, volume: d.volume,
      source: d.source || "",
      raw: d,
      receivedAt: new Date().toISOString(),
    };
  }
  if (obj && obj.eventType === "ping") return null;

  const p = obj || {};
  return {
    id: p.id ?? undefined,
    decision: p.decision || p.side || p.signal || "",
    ticker: p.ticker || p.symbol || "",
    exchange: p.exchange || "",
    interval: p.interval || "",
    time: p.time || p.event_time || p.timenow || p.timestamp || "",
    open: p.open, high: p.high, low: p.low, close: p.close, volume: p.volume,
    source: p.source || "",
    raw: p,
    receivedAt: new Date().toISOString(),
  };
}

function tvUrl(exchange, ticker) {
  const ex = String(exchange || "").trim();
  const tk = String(ticker || "").trim();
  if (!ex || !tk) return `https://in.tradingview.com/chart/${TV_CHART_ID}/`;
  return `https://in.tradingview.com/chart/${TV_CHART_ID}/?symbol=${encodeURIComponent(`${ex}:${tk}`)}`;
}

const clampInt = (v, min, max, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.trunc(n);
  return Math.min(Math.max(i, min), max);
};

export default function WebSocketTradeTable() {
  // user limits
  const [tableLimitInput, setTableLimitInput] = useState("100");
  const [dbFetchLimitInput, setDbFetchLimitInput] = useState("10");
  const tableLimit = clampInt(tableLimitInput, 1, 500, 100);
  const dbFetchLimit = clampInt(dbFetchLimitInput, 1, 500, 10);
  const tableLimitValid = /^\d+$/.test(tableLimitInput) && +tableLimitInput >= 1 && +tableLimitInput <= 500;
  const dbFetchLimitValid = /^\d+$/.test(dbFetchLimitInput) && +dbFetchLimitInput >= 1 && +dbFetchLimitInput <= 500;

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("connecting"); // connecting|open|closed|error
  const [input, setInput] = useState("");

  // WS controls
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const backoffRef = useRef(1000);
  const unmountedRef = useRef(false);

  // Sound + Desktop notifications
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("barq:soundEnabled") !== "0");
  const [notifyEnabled, setNotifyEnabled] = useState(() => localStorage.getItem("barq:notifyEnabled") === "1");
  const [notifyWhenActive, setNotifyWhenActive] = useState(() => localStorage.getItem("barq:notifyWhenActive") === "1");
  const [volume, setVolume] = useState(() => {
    const v = Number(localStorage.getItem("barq:soundVolume"));
    return Number.isFinite(v) ? Math.min(Math.max(v, 0), 1) : 0.7;
  });

  const audioRef = useRef(null);
  const audioReadyRef = useRef(false);

  // Audio prep
  useEffect(() => {
    const audio = new Audio(ALERT_SOUND_URL);
    audio.preload = "auto";
    audio.volume = volume;
    audioRef.current = audio;

    const unlock = () => {
      audioReadyRef.current = true;
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);

    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []); // eslint-disable-line

  // Persist prefs
  useEffect(() => { localStorage.setItem("barq:soundEnabled", soundEnabled ? "1" : "0"); }, [soundEnabled]);
  useEffect(() => { localStorage.setItem("barq:notifyEnabled", notifyEnabled ? "1" : "0"); }, [notifyEnabled]);
  useEffect(() => { localStorage.setItem("barq:notifyWhenActive", notifyWhenActive ? "1" : "0"); }, [notifyWhenActive]);
  useEffect(() => {
    localStorage.setItem("barq:soundVolume", String(volume));
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Prompt for permission the moment user enables Desktop notifications
  useEffect(() => {
    if (!notifyEnabled) return;
    if (!("Notification" in window)) {
      console.warn("Notifications not supported in this browser.");
      return;
    }
    if (Notification.permission === "default") {
      Notification.requestPermission().then((perm) => {
        if (perm !== "granted") {
          console.warn("Desktop notifications permission was not granted.");
        }
      });
    }
  }, [notifyEnabled]);

  const playAlertSound = () => {
    if (!soundEnabled || !audioReadyRef.current) return;
    try {
      const a = audioRef.current.cloneNode(true);
      a.volume = volume;
      a.play().catch(() => {});
    } catch {}
  };

  const notifyDesktop = (title, body) => {
    if (!notifyEnabled || !("Notification" in window)) return false;

    const show = () => {
      try {
        // Tag collapses duplicates, renotify pops it again on some browsers
        new Notification(title, {
          body,
          tag: "barqlytics-alert",
          renotify: true,
          // icon: "/icons/barq-128.png", // optional if you have one
        });
        return true;
      } catch {
        return false;
      }
    };

    if (Notification.permission === "granted") return show();
    if (Notification.permission === "denied") return false;

    // default → ask once
    Notification.requestPermission().then((perm) => {
      if (perm === "granted") show();
    });
    return false;
  };

  // Initial REST load (newest first on top)
  const loadFromAPI = async (limit) => {
    const res = await fetch(`${REST_ALERTS_BASE}?limit=${encodeURIComponent(limit)}`, {
      method: "GET",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (Array.isArray(data) ? data : []).map((d) =>
      normalizeFromEnvelope({ eventType: "alert", data: d })
    );
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await loadFromAPI(dbFetchLimit);
        if (alive) setRows(data.slice(0, tableLimit));
      } catch (e) {
        console.warn("Initial REST load failed:", e);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // once

  // Trim immediately if user lowers tableLimit
  useEffect(() => { setRows((prev) => prev.slice(0, tableLimit)); }, [tableLimit]);

  // WebSocket: backoff, pings, strict envelope handling, dedupe by id
  useEffect(() => {
    unmountedRef.current = false;

    const connect = () => {
      setStatus("connecting");
      const ws = new WebSocket(WEBSOCKET_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (unmountedRef.current) return;
        console.log("[WS] open", WEBSOCKET_URL);
        setStatus("open");
        backoffRef.current = 1000;
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try { ws.readyState === WebSocket.OPEN && ws.send('{"eventType":"ping"}'); } catch {}
        }, 25000);
      };

      ws.onmessage = (evt) => {
        if (unmountedRef.current) return;
        const obj = tryParsePayload(evt.data);
        if (!obj || (obj.eventType && obj.eventType !== "alert")) return;

        const stamped = normalizeFromEnvelope(obj);
        if (!stamped) return;

        setRows((prev) => {
          if (stamped.id != null && prev.some((r) => r.id === stamped.id)) return prev;
          return [stamped, ...prev].slice(0, tableLimit);
        });

        // Sound + Desktop notification
        const dec = String(stamped.decision || "").toLowerCase();
        if (dec === "buy" || dec === "sell") {
          playAlertSound();
          if (document.hidden || notifyWhenActive) {
            const title = stamped.ticker || "Barqlytics Alert";
            const body = `${dec.toUpperCase()} @ ${stamped.close ?? ""} (${stamped.interval || ""})`;
            notifyDesktop(title, body);
          }
        }
      };

      ws.onerror = (e) => {
        if (unmountedRef.current) return;
        console.warn("[WS] error", e);
        setStatus("error");
      };

      ws.onclose = (evt) => {
        if (unmountedRef.current) return;
        console.warn("[WS] close", { code: evt.code, reason: evt.reason, wasClean: evt.wasClean });
        setStatus("closed");
        if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
        if (!reconnectTimerRef.current) {
          const jitter = Math.floor(Math.random() * 400);
          const delay = Math.min(backoffRef.current + jitter, 15000);
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            backoffRef.current = Math.min(backoffRef.current * 2, 15000);
            connect();
          }, delay);
        }
      };
    };

    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.close(1000, "component unmount"); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableLimit]);

  const send = () => {
    if (!input.trim()) return;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(input);
      setInput("");
    } else {
      console.warn("WS not open; cannot send");
    }
  };

  const refreshFromAPI = async () => {
    if (!dbFetchLimitValid) return;
    try {
      const data = await loadFromAPI(dbFetchLimit);
      setRows(data.slice(0, tableLimit));
    } catch (e) {
      console.warn("Refresh failed:", e);
    }
  };

  const statusIcon = useMemo(() => {
    switch (status) {
      case "open":       return <span className="text-green-500"><i className="fa-solid fa-circle-check" /> Connected</span>;
      case "connecting": return <span className="text-amber-500"><i className="fa-solid fa-rotate" /> Connecting…</span>;
      case "error":      return <span className="text-red-500"><i className="fa-solid fa-triangle-exclamation" /> Error</span>;
      case "closed":
      default:           return <span className="text-slate-500"><i className="fa-regular fa-circle-xmark" /> Disconnected</span>;
    }
  }, [status]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between shadow">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBroadcastTower} />
          <h1 className="font-semibold">Barqlytics — Live Alerts</h1>
        </div>
        <div className="text-sm">{statusIcon}</div>
      </header>

      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-3 flex flex-wrap items-center gap-3">
        {/* Sound + Notifications */}
        <div className="flex items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} />
            <span className="inline-flex items-center gap-1"><i className="fa-solid fa-bell" /> Sound</span>
          </label>

          <div className="flex items-center gap-2">
            <span className="text-slate-600">Volume</span>
            <input
              type="range" min={0} max={1} step={0.05}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="w-28"
              title={`Volume: ${(volume * 100) | 0}%`}
            />
          </div>

          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={notifyEnabled}
              onChange={(e) => setNotifyEnabled(e.target.checked)}
            />
            <span className="inline-flex items-center gap-1"><i className="fa-regular fa-bell" /> Desktop</span>
          </label>

          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={notifyWhenActive}
              onChange={(e) => setNotifyWhenActive(e.target.checked)}
            />
            <span className="inline-flex items-center gap-1">Notify when active</span>
          </label>

          <button
            onClick={() => notifyDesktop("Barqlytics — Test", "If you see this, desktop notifications are working.")}
            className="border px-2 py-1 rounded text-xs hover:bg-slate-50"
            title="Send a test desktop notification"
          >
            Test
          </button>
        </div>

        {/* Table size */}
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Table Size</span>
          <input
            type="number" min={1} max={500}
            value={tableLimitInput}
            onChange={(e) => setTableLimitInput(e.target.value)}
            className={`w-24 border rounded-lg px-2 py-1 outline-none focus:ring-2 ${
              tableLimitValid ? "focus:ring-slate-500" : "border-red-500 focus:ring-red-500"
            }`}
            title="Number of rows to keep (1–500)"
          />
          {!tableLimitValid && <span className="text-red-600 text-xs">Enter 1–500</span>}
        </label>

        {/* DB fetch size */}
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">DB Fetch</span>
          <input
            type="number" min={1} max={500}
            value={dbFetchLimitInput}
            onChange={(e) => setDbFetchLimitInput(e.target.value)}
            className={`w-24 border rounded-lg px-2 py-1 outline-none focus:ring-2 ${
              dbFetchLimitValid ? "focus:ring-slate-500" : "border-red-500 focus:ring-red-500"
            }`}
            title="Records to fetch from DB (1–500)"
          />
          {!dbFetchLimitValid && <span className="text-red-600 text-xs">Enter 1–500</span>}
        </label>

        <button
          onClick={refreshFromAPI}
          disabled={!dbFetchLimitValid}
          className={`ml-auto border px-3 py-1.5 rounded-lg transition text-sm flex items-center gap-2 ${
            dbFetchLimitValid ? "bg-white hover:bg-slate-50" : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          title="Reload from database (replaces table)"
        >
          <i className="fa-solid fa-rotate-right" /> Refresh
        </button>

        {/* Test send (for dev) */}
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <input
            className="w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-slate-500"
            placeholder='Send a test (e.g. {"eventType":"alert","data":{"decision":"buy","exchange":"NSE","ticker":"INFY","close":1620.4}})'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
          />
          <button
            onClick={send}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-slate-900 text-white px-3 py-1.5 rounded-lg hover:bg-black transition"
            title="Send to WebSocket"
          >
            <i className="fa-solid fa-paper-plane" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="p-4 overflow-auto">
        <div className="bg-white rounded-xl shadow ring-1 ring-black/5">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold flex items-center gap-2">
              <i className="fa-solid fa-table" />
              Latest Alerts
            </div>
            <div className="text-xs text-slate-500">Showing {rows.length} / {tableLimit}</div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr className="[&>th]:px-3 [&>th]:py-2 text-left">
                  <th>Decision</th><th>Ticker</th>
                  <th className="w-28 text-center">Actions</th>
                  {/* NEW columns start */}
                  <th>Alert Type</th>
                  <th>Pivot</th>
                  <th>VWAP</th>
                  <th>MACD L/S</th>
                  <th>EMA50</th>
                  <th>BB U/L</th>
                  <th>ST U/D</th>
                  <th>CM MA1/MA2</th>
                  <th>Trend</th>
                  {/* NEW columns end */}
                  <th>Close</th><th>O / H / L</th><th>Volume</th>
                  <th>Interval</th><th>Exchange</th><th>Time</th>
                  {/* NEW: Report Time */}
                  <th>Report Time</th>
                 
                  <th className="w-12 text-center" title="View raw JSON"><i className="fa-solid fa-code" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => {
                  const url = tvUrl(r.exchange, r.ticker);
                  const isBuy = String(r.decision || "").toLowerCase() === "buy";
                  const isSell = String(r.decision || "").toLowerCase() === "sell";

                  // Pull extras from either top-level (if present) or raw payload.
                  const raw = r.raw || {};
                  const alertType = r.alert_type || raw.alert_type;
                  const pivot = r.pivot_type || raw.pivot_type;
                  const vwap = r.vwap_price ?? raw.vwap_price;
                  const macdL = r.macd_line ?? raw.macd_line;
                  const macdS = r.macd_signal ?? raw.macd_signal;
                  const ema50 = r.ema_50 ?? raw.ema_50;
                  const bbU = r.bb_upper ?? raw.bb_upper;
                  const bbL = r.bb_lower ?? raw.bb_lower;
                  const stU = r.supertrend_up ?? raw.supertrend_up;
                  const stD = r.supertrend_dn ?? raw.supertrend_dn;
                  const cm1 = r.cm_ma1 ?? raw.cm_ma1;
                  const cm2 = r.cm_ma2 ?? raw.cm_ma2;
                  const trendColor = r.trend_color || raw.trend_color;
                  const trendDir = r.trend_direction || r.trend || r.swing_direction || raw.trend_direction || raw.trend || raw.swing_direction;

                  const reportTime = r.report_time || raw.report_time;
                  const eventTime = r.time || r.event_time || raw.event_time;

                  return (
                    <tr key={(r.id ?? r.receivedAt) + "_" + i} className={`${decisionClasses(r.decision)} [&>td]:px-3 [&>td]:py-2`}>
                      <td><span className={decisionBadge(r.decision)}>{String(r.decision || "n/a").toUpperCase()}</span></td>
                      <td className="font-medium">{r.ticker || "—"}</td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className={`px-2 py-1 rounded-md text-white text-xs font-semibold inline-flex items-center gap-1 ${
                              isBuy ? "bg-green-600 hover:bg-green-700"
                              : isSell ? "bg-red-600 hover:bg-red-700"
                              : "bg-slate-600 hover:bg-slate-700"
                            }`}
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square" />
                            {isBuy ? "Buy" : isSell ? "Sell" : "Chart"}
                          </a>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-2 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold inline-flex items-center gap-1"
                          >
                            <i className="fa-solid fa-chart-line" /> Chart
                          </a>
                        </div>
                      </td>
                      {/* NEW cells start */}
                      <td>{alertType || "—"}</td>
                      <td>{pivot || "—"}</td>
                      <td>{prettyNum(vwap)}</td>
                      <td className="whitespace-nowrap">
                        <span className="text-slate-700">{prettyNum(macdL)}</span>
                        <span className="text-slate-400"> / </span>
                        <span className="text-slate-700">{prettyNum(macdS)}</span>
                      </td>
                      <td>{prettyNum(ema50)}</td>
                      <td className="whitespace-nowrap">
                        <span className="text-slate-700">{prettyNum(bbU)}</span>
                        <span className="text-slate-400"> / </span>
                        <span className="text-slate-700">{prettyNum(bbL)}</span>
                      </td>
                      <td className="whitespace-nowrap">
                        <span className="text-slate-700">{prettyNum(stU)}</span>
                        <span className="text-slate-400"> / </span>
                        <span className="text-slate-700">{prettyNum(stD)}</span>
                      </td>
                      <td className="whitespace-nowrap">
                        <span className="text-slate-700">{prettyNum(cm1)}</span>
                        <span className="text-slate-400"> / </span>
                        <span className="text-slate-700">{prettyNum(cm2)}</span>
                      </td>
                      <td className="whitespace-nowrap">
                        {(trendColor ? `${trendColor}` : "")}
                        {trendDir ? (trendColor ? ` • ${trendDir}` : trendDir) : (trendColor ? "" : "—")}
                      </td>
                      {/* NEW cells end */}

                      <td>{prettyNum(r.close)}</td>
                      <td>
                        <span className="text-slate-700">{prettyNum(r.open)}</span>{" "}
                        <span className="text-slate-400">/</span>{" "}
                        <span className="text-slate-700">{prettyNum(r.high)}</span>{" "}
                        <span className="text-slate-400">/</span>{" "}
                        <span className="text-slate-700">{prettyNum(r.low)}</span>
                      </td>
                      <td>{prettyNum(r.volume)}</td>
                      <td>{r.interval || "—"}</td>
                      <td>{r.exchange || "—"}</td>
                      <td className="whitespace-nowrap">{eventTime ? new Date(eventTime).toLocaleString() : "—"}</td>
                      {/* NEW: Report Time */}
                      <td className="whitespace-nowrap">{reportTime ? new Date(reportTime).toLocaleString() : "—"}</td>

                   
                      <td className="text-center">
                        <details>
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-900">
                            <i className="fa-solid fa-eye" />
                          </summary>
                          <pre className="text-xs bg-slate-100 p-2 rounded mt-1 max-h-60 overflow-auto">
{JSON.stringify(r.raw ?? r, null, 2)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={20} className="text-center text-slate-500 py-10">Waiting for messages…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="px-4 py-3 text-xs text-slate-500">
        <i className="fa-regular fa-circle-question" /> Turn on <b>Desktop</b> and click <b>Test</b> to verify notifications.  
        If nothing shows, make sure your browser/site notification permissions are allowed.
      </footer>
    </div>
  );
}